import { useState, FormEvent } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    projectType: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d5i4jr9ia7n1eqcqusmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          address: '',
          projectType: '',
          description: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    'Concrete & Patios',
    'Screened-In Porch',
    'Fence Installation',
    'Pergola / Patio Cover',
    'Interior Remodeling',
    'General Home Improvement',
    'Other',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[40vh] sm:h-[45vh] md:h-[50vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d0da8d0c0512e9b06f30830d0212732c.jpg"
            alt="Request a Quote"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 w-full text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-tight mb-3 sm:mb-4 md:mb-6"
          >
            REQUEST A QUOTE
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-neutral-lighter-gray max-w-3xl mx-auto px-4"
          >
            Get a detailed, transparent estimate for your project
          </motion.p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-28">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-neutral-charcoal mb-4 sm:mb-5 md:mb-6">
                LET'S DISCUSS YOUR PROJECT
              </h2>
              <p className="text-base sm:text-lg text-neutral-gray mb-6 sm:mb-7 md:mb-8 leading-relaxed">
                Fill out the form and we'll contact you within 24 hours to schedule a site visit and provide a detailed estimate.
              </p>

              <div className="space-y-5 sm:space-y-6 mb-6 sm:mb-7 md:mb-8">
                <div className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-red rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-phone-fill text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-neutral-charcoal mb-1">Phone</h3>
                    <a
                      href="tel:+14692544779"
                      className="text-sm sm:text-base text-neutral-gray hover:text-primary-red transition-colors cursor-pointer"
                    >
                      (469) 254-4779
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-red rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-mail-fill text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-neutral-charcoal mb-1">Email</h3>
                    <a
                      href="mailto:vantexconstruction@gmail.com"
                      className="text-sm sm:text-base text-neutral-gray hover:text-primary-red transition-colors cursor-pointer break-all"
                    >
                      vantexconstruction@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-red rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-map-pin-fill text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-neutral-charcoal mb-1">Service Area</h3>
                    <p className="text-sm sm:text-base text-neutral-gray">Dallas-Fort Worth Metroplex</p>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-bg-gray p-5 sm:p-6 rounded-lg">
                <h3 className="text-lg sm:text-xl font-bold text-neutral-charcoal mb-3 sm:mb-4">What to Expect</h3>
                <ul className="space-y-2.5 sm:space-y-3">
                  {[
                    'Response within 24 hours',
                    'Free on-site consultation',
                    'Detailed written estimate',
                    'Clear project timeline',
                    'No hidden fees or surprises',
                  ].map((item) => (
                    <li key={item} className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-primary-red mr-2.5 sm:mr-3 mt-0.5 sm:mt-1 text-base sm:text-lg w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                      <span className="text-sm sm:text-base text-neutral-gray">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <form
                id="quote-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-neutral-bg-gray p-5 sm:p-6 md:p-8 rounded-lg"
              >
                <div className="space-y-5 sm:space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-neutral-charcoal mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-neutral-lighter-gray rounded-md focus:outline-none focus:border-primary-red transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-neutral-charcoal mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-neutral-lighter-gray rounded-md focus:outline-none focus:border-primary-red transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-neutral-charcoal mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-neutral-lighter-gray rounded-md focus:outline-none focus:border-primary-red transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-bold text-neutral-charcoal mb-2">
                      Project Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-neutral-lighter-gray rounded-md focus:outline-none focus:border-primary-red transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="projectType" className="block text-sm font-bold text-neutral-charcoal mb-2">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-neutral-lighter-gray rounded-md focus:outline-none focus:border-primary-red transition-colors cursor-pointer"
                    >
                      <option value="">Select a project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-bold text-neutral-charcoal mb-2">
                      Project Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-neutral-lighter-gray rounded-md focus:outline-none focus:border-primary-red transition-colors resize-none"
                      placeholder="Tell us about your project..."
                    ></textarea>
                    <p className="text-xs text-neutral-gray mt-1">
                      {formData.description.length}/500 characters
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary-red hover:bg-primary-red-dark text-white px-6 sm:px-8 py-3 sm:py-4 font-bold text-sm sm:text-base tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer rounded-md"
                  >
                    {isSubmitting ? 'SUBMITTING...' : 'REQUEST FREE ESTIMATE'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm sm:text-base">
                      Thank you! We'll contact you within 24 hours.
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm sm:text-base">
                      Something went wrong. Please try again or call us directly.
                    </div>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuotePage;